import {
  ArrayInput,
  AutocompleteArrayInput,
  AutocompleteInput,
  BooleanField,
  BooleanInput,
  Create,
  CreateButton,
  Datagrid,
  DateField,
  DateTimeInput,
  DeleteButton,
  Edit,
  EditButton,
  Form,
  FormDataConsumer,
  FunctionField,
  List,
  NumberField,
  NumberInput,
  Pagination,
  ReferenceArrayField,
  ReferenceArrayInput,
  ReferenceField,
  ReferenceInput,
  SaveButton,
  SelectInput,
  Show,
  ShowButton,
  SimpleForm,
  SimpleFormIterator,
  SimpleShowLayout,
  SingleFieldList,
  TextField,
  TextInput,
  maxLength,
  required,
  useGetOne,
  useRecordContext,
  useRefresh,
  useUpdate,
  type RaRecord,
} from "react-admin";
import {
  Box,
  Button,
  Chip,
  FormControlLabel,
  Stack,
  Switch,
  TextField as MuiTextField,
  Typography,
  useTheme,
} from "@mui/material";
import { lazy, Suspense, useState, type ReactElement, type ReactNode } from "react";
import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";

type TranslationKeyRecord = RaRecord & {
  isRichText?: boolean;
  isRequired?: boolean;
  valueType?: string;
  translations?: { locale: string; value: unknown; isPublished: boolean }[];
  existingLocales?: string[];
  translatedLocales?: string[];
  missingLocales?: string[];
  unpublishedLocales?: string[];
  needsAttentionLocales?: string[];
  translationStatus?: "complete" | "needsAttention";
};
type PublicTranslationRecord = RaRecord & {
  keyId: string;
  locale: string;
  value: unknown;
  isPublished: boolean;
};
const publicLocaleChoices = [
  { id: "en-GB", name: "English" },
  { id: "ja-JP", name: "Japanese" },
];
const valueTypeChoices = [
  { id: "text", name: "Text" },
  { id: "image", name: "Image" },
  { id: "json", name: "JSON" },
];
const RowActions = () => (
  <>
    <ShowButton />
    <EditButton />
    <DeleteButton mutationMode="pessimistic" />
  </>
);
const TranslationKeyForm = ({ includeId = false }: { includeId?: boolean }) => (
  <SimpleForm resetOptions={{ keepDirtyValues: true }}>
    {includeId ? (
      <TextInput
        source="id"
        label="Key"
        validate={[required(), maxLength(160)]}
        helperText="Unique translation key, e.g. welcome.title"
        fullWidth
      />
    ) : null}
    <TextInput source="namespace" validate={[required(), maxLength(64)]} disabled={!includeId} fullWidth />
    <SelectInput
      source="valueType"
      label="Type"
      choices={valueTypeChoices}
      defaultValue="text"
      validate={required()}
      disabled={!includeId}
    />
    <BooleanInput source="isRequired" label="Required" defaultValue={true} />
    {includeId ? (
      <FormDataConsumer>
        {({ formData }) => (
          <BooleanInput
            source="isRichText"
            label="Rich text (all languages)"
            defaultValue={false}
            disabled={formData.valueType !== "text"}
          />
        )}
      </FormDataConsumer>
    ) : null}
    <TextInput source="description" multiline minRows={2} fullWidth />
  </SimpleForm>
);
const TranslationRichTextInput = lazy(() => import("./admin/TranslationRichTextInput"));
const translationValuePreview = (value: unknown) =>
  value == null ? "" : typeof value === "string" ? value : JSON.stringify(value, null, 2);
const InlinePublicTranslationEditor = ({
  translation,
  isRichText,
  valueType,
  isRequired,
}: {
  translation: PublicTranslationRecord;
  isRichText?: boolean;
  valueType?: string;
  isRequired?: boolean;
}) => {
  const [update, { error, isLoading }] = useUpdate();
  const refresh = useRefresh();
  const [valueError, setValueError] = useState("");
  const save = async (values: Record<string, unknown>) => {
    setValueError("");
    let value = values.value;
    if (valueType === "json" && typeof value === "string") {
      try {
        value = JSON.parse(value);
      } catch {
        setValueError("Enter valid JSON before saving.");
        return;
      }
    }
    await update(
      "publicTranslations",
      {
        id: translation.id,
        data: { keyId: translation.keyId, locale: translation.locale, value, isPublished: translation.isPublished },
        previousData: translation,
      },
      { mutationMode: "pessimistic", returnPromise: true, onSuccess: refresh }
    );
  };

  return (
    <Box sx={{ border: 1, borderColor: "divider", borderRadius: 1, p: 2 }}>
      <Form record={translation} onSubmit={save} resource="publicTranslations">
        <Stack spacing={1.5}>
          <Stack direction="row" alignItems="center" justifyContent="space-between">
            <Typography variant="subtitle2">
              {publicLocaleChoices.find((choice) => choice.id === translation.locale)?.name ?? translation.locale}
            </Typography>
            <Chip
              label={translation.isPublished ? "Published" : "Draft"}
              color={translation.isPublished ? "success" : "warning"}
              size="small"
              variant="outlined"
            />
          </Stack>
          {isRichText && valueType === "text" ? (
            <Suspense fallback={<Typography>Loading editor…</Typography>}>
              <TranslationRichTextInput
                source="value"
                label="Translation"
                validate={isRequired ? required() : undefined}
                fullWidth
              />
            </Suspense>
          ) : (
            <TextInput
              source="value"
              label="Translation"
              format={translationValuePreview}
              validate={isRequired ? required() : undefined}
              multiline
              minRows={3}
              fullWidth
            />
          )}
          {valueError ? (
            <Typography color="error" variant="body2">
              {valueError}
            </Typography>
          ) : null}
          {error ? (
            <Typography color="error" variant="body2">
              {error instanceof Error ? error.message : String(error)}
            </Typography>
          ) : null}
          <Box>
            <SaveButton disabled={isLoading} />
          </Box>
        </Stack>
      </Form>
    </Box>
  );
};
const TranslationKeyTranslationsPanel = () => {
  const record = useRecordContext<TranslationKeyRecord>();
  const [update, { error, isLoading }] = useUpdate();
  if (!record) {
    return null;
  }
  const locales = Array.from(
    new Set([
      ...publicLocaleChoices.map((choice) => choice.id),
      ...(record.translations ?? []).map((item) => item.locale),
    ])
  );

  return (
    <Box sx={{ px: 2, pb: 2 }}>
      <Typography variant="h6" sx={{ mb: 1 }}>
        Translations
      </Typography>
      {record.valueType === "text" ? (
        <Box sx={{ mb: 2 }}>
          <FormControlLabel
            label="Rich text (all languages)"
            control={
              <Switch
                checked={Boolean(record.isRichText)}
                disabled={isLoading}
                onChange={(_event, isRichText) =>
                  update(
                    "translationKeys",
                    { id: record.id, data: { isRichText }, previousData: record },
                    { mutationMode: "pessimistic" }
                  )
                }
              />
            }
          />
          <Typography variant="caption" color="text.secondary" display="block">
            Saves immediately. Turning this off keeps existing HTML available in the text input.
          </Typography>
          {error ? (
            <Typography variant="body2" color="error">
              {error instanceof Error ? error.message : "Rich-text setting could not be saved."}
            </Typography>
          ) : null}
        </Box>
      ) : null}
      <Stack spacing={1.5}>
        {locales.map((locale) => {
          const existing = record.translations?.find((translation) => translation.locale === locale);
          return (
            <InlinePublicTranslationEditor
              key={locale}
              translation={{
                id: `${record.id}:${locale}`,
                keyId: String(record.id),
                locale,
                value: existing?.value ?? "",
                isPublished: existing?.isPublished ?? true,
              }}
              isRichText={record.isRichText}
              valueType={record.valueType}
              isRequired={record.isRequired}
            />
          );
        })}
      </Stack>
    </Box>
  );
};

const TranslationKeyListTranslationsExpand = () => <TranslationKeyTranslationsPanel />;
const TranslationLocaleStatusField = ({ locale }: { locale: string; label?: string }) => {
  const record = useRecordContext<TranslationKeyRecord>();
  const value = record?.translations?.find((item) => item.locale === locale)?.value;
  const ready =
    value != null &&
    (typeof value === "string"
      ? value.trim() !== ""
      : typeof value === "object"
      ? Object.keys(value).length > 0
      : value !== "");

  return (
    <Chip label={ready ? "Ready" : "Missing"} color={ready ? "success" : "error"} size="small" variant="outlined" />
  );
};
const translationStatusChoices = [
  { id: "needsAttention", name: "Needs attention" },
  { id: "complete", name: "Complete" },
];
const translationKeyFilters = [
  <TextInput key="search" source="q" label="Search keys or text" alwaysOn resettable />,
  <SelectInput
    key="status"
    source="translationStatus"
    label="Translation status"
    choices={translationStatusChoices}
    alwaysOn
  />,
  <SelectInput key="locale" source="needsAttentionLocales" label="Language" choices={publicLocaleChoices} alwaysOn />,
];
export const TranslationKeyList = () => (
  <List
    filters={translationKeyFilters}
    sort={{ field: "namespace", order: "ASC" }}
    perPage={50}
    pagination={<Pagination rowsPerPageOptions={[50, 500, 5000]} />}
  >
    <Datagrid bulkActionButtons={false} rowClick="show" expand={<TranslationKeyListTranslationsExpand />}>
      <TextField source="id" label="Key" />
      <TextField source="namespace" />
      <TranslationLocaleStatusField label="English" locale="en-GB" />
      <TranslationLocaleStatusField label="Japanese" locale="ja-JP" />
      <TextField source="valueType" label="Type" />
      <BooleanField source="isRichText" label="Rich text" />
      <BooleanField source="isRequired" label="Required" />
      <TextField source="description" />
      <RowActions />
    </Datagrid>
  </List>
);

export const TranslationKeyShow = () => (
  <Show>
    <SimpleShowLayout>
      <TextField source="id" label="Key" />
      <TextField source="namespace" />
      <TranslationLocaleStatusField label="English" locale="en-GB" />
      <TranslationLocaleStatusField label="Japanese" locale="ja-JP" />
      <TextField source="valueType" label="Type" />
      <BooleanField source="isRichText" label="Rich text" />
      <BooleanField source="isRequired" label="Required" />
      <TextField source="description" />
    </SimpleShowLayout>
    <TranslationKeyTranslationsPanel />
  </Show>
);

export const TranslationKeyCreate = () => (
  <Create
    transform={(data: Record<string, unknown>) => ({
      ...data,
      isRichText: data.valueType === "text" && Boolean(data.isRichText),
    })}
  >
    <TranslationKeyForm includeId />
  </Create>
);
export const TranslationKeyEdit = () => (
  <Edit mutationMode="pessimistic">
    <TranslationKeyForm />
    <TranslationKeyTranslationsPanel />
  </Edit>
);
